import React, { useContext, useEffect, useState } from "react";
import {Accordion,	AccordionDetails, AccordionSummary,	FormControlLabel,	Grid, Radio, Typography, useTheme} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useFormContext, useForm} from "react-hook-form";
import VehicleDetails from "../vehicle-details/VehicleDetails";
import {IRaFillingForm, TYesNo} from "../../../models/models";
import {FormAutocomplete, FormDateTimePicker, FormRadioGroup, FormTextField} from "../../../common/form-fields-wrappers";
import {useRecoilState, useRecoilValue} from "recoil";
import {bookingDataValueState, counterSaleManagementValueState, raFillingAccordionValidationState} from "../../state";
import {deepClone} from "../../../../../util/state/commonFunctions";
import {FINANCIAL_CREDIT_TYPES} from "../../../config";
import { generalInfoState } from "../../../search-dms/state";
import { getMobilityStoreBySapId } from "../../../search-dms/dmsSearch.actions";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import { AxiosError } from "axios";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";

const GeneralInfo = (props: { keyLess: boolean | undefined, keyLessInfo: any }) => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const {dispatchGlobalData} = useContext(GlobalContext);

	const raFillingDataForm = useFormContext<IRaFillingForm>();
	const [counterSaleManagement, setCounterSaleManagement] = useRecoilState(counterSaleManagementValueState);
	const bookingDataValue = useRecoilValue(bookingDataValueState);
	const [raFillingAccordionValidation, setRaFillingAccordionValidation] = useRecoilState(raFillingAccordionValidationState);
	const [dmsGeneralInfo, setDmsGeneralInfo] = useState<any>(null);

	const keyLessData = useForm<{ keyLess: TYesNo, keyLessInfo: string }>({
		defaultValues: {
			keyLess: props.keyLess ? 'YES' : 'NO',
			keyLessInfo: props?.keyLessInfo ?? '',
		}
	});

	/*
	useEffect(() => {
		keyLessData.setValue("keyLess", props.keyLess ? 'YES' : 'NO');
		keyLessData.setValue("keyLessInfo", props.keyLessInfo?.keyLessInfo ?? '');
	}, [props.keyLess, bookingDataValue]);
	*/
	useEffect(() => {
		keyLessData.setValue("keyLess", props.keyLess ? 'YES' : 'NO');
		keyLessData.setValue("keyLessInfo", props?.keyLessInfo ?? '');
	}, []);


	// based on YES or NO value on UpSell Field
	const handleUpSell = (event: React.BaseSyntheticEvent) => {
		const upSell = event?.target?.value;
		const acriss = raFillingDataForm.getValues('vehicleDetails.acriss');

		!!upSell ?
			upSell === "NO" ?
				raFillingDataForm.setValue('generalInfo.billingCarGroup', raFillingDataForm.getValues('generalInfo.bookedCarGroup')) :
				raFillingDataForm.setValue('generalInfo.billingCarGroup',!!acriss?.description ? `${acriss.code} - ${acriss.description}` :	'')
			:
				raFillingDataForm.setValue('generalInfo.billingCarGroup', '');

		if (!!counterSaleManagement?.last) {
			const csmClone = deepClone(counterSaleManagement.last);
			csmClone.upsell = upSell === "YES";
			setCounterSaleManagement({previous: counterSaleManagement.last, last: csmClone});
		}
	}

	const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean) => {
		e.stopPropagation();
		
		setRaFillingAccordionValidation((prev) => {
			return {...prev, ["generalInfo"]: expanded};
		});
	}

	return (
		<>
			<Accordion expanded={raFillingAccordionValidation["generalInfo"]} onChange={(e, expanded) => handleAccordionOnChange(e, expanded)}> 
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Typography variant={"h6"}>
						{t("raFilling.generalInfo.accordion.title")}
					</Typography>
				</AccordionSummary>

				<AccordionDetails>
					<Typography variant={"bodyLarge"}>
						{t("raFilling.generalInfo.form.title")}
					</Typography>

					<Grid container columnSpacing={2} sx={{mb: theme.spacing(2)}}>
						<Grid item container xs={12} md={6} sx={{mb: 'auto'}} columnSpacing={2}>

							<Grid item xs={6}>
								<FormAutocomplete<IRaFillingForm>
									name="generalInfo.pickupLocation"
									label={t("raFilling.generalInfo.form.label.pickupLocation")}
									control={raFillingDataForm.control}
									fullWidth
									options={[]}
									getOptionLabel={(opt) => opt?.label}
									isOptionEqualToValue={(option, value) => option?.code === value?.code}
									disabled
								/>
							</Grid>
							<Grid item xs={6}>
								<FormDateTimePicker<IRaFillingForm>
									name="generalInfo.expectedPickupDateTime"
									label={t("raFilling.generalInfo.form.label.pickupDateTime")}
									control={raFillingDataForm.control}
									disabled
								/>
							</Grid>

							<Grid item xs={6}>
								<FormAutocomplete<IRaFillingForm>
									name="generalInfo.dropoffLocation"
									label={t("raFilling.generalInfo.form.label.dropoffLocation")}
									control={raFillingDataForm.control}
									options={[]}
									getOptionLabel={(opt) => opt?.label}
									isOptionEqualToValue={(option, value) => option?.code === value?.code}
									disabled
								/>
							</Grid>
							<Grid item xs={6}>
								<FormDateTimePicker<IRaFillingForm>
									name="generalInfo.expectedDropoffDateTime"
									label={t("raFilling.generalInfo.form.label.expectedDropoffDateTime")}
									control={raFillingDataForm.control}
									disabled
								/>
							</Grid>

							<Grid item xs={6}>
								<FormTextField<IRaFillingForm>
									name="generalInfo.expectedTotRentalDays"
									label={t("raFilling.generalInfo.form.label.expectedTotRentalDays")}
							    	control={raFillingDataForm.control}
									disabled
								/>
							</Grid>
						</Grid>

						<Grid item container xs={12} md={6} columnSpacing={2}>

							<Grid item xs={6}>
								<FormTextField<IRaFillingForm>
									name="generalInfo.bookedCarGroup"
									label={t("raFilling.generalInfo.form.label.bookedAcriss")}
									control={raFillingDataForm.control}
									fullWidth
									disabled
								/>
							</Grid>

							<Grid item xs={6}>
								<FormTextField<IRaFillingForm>
									name="generalInfo.bookedVehicleType"
									label={t("raFilling.generalInfo.form.label.bookedVehicleType")}
									control={raFillingDataForm.control}
									disabled
								/>
							</Grid>

							{props.keyLess || <Grid item xs={6}>
								<FormRadioGroup<IRaFillingForm>
									name="generalInfo.upSell"
									label={t('raFilling.generalInfo.form.label.upSell')}
									onChange={handleUpSell}
									disabled={bookingDataValue.bookingDetail.flagSubstitution || bookingDataValue.financialInfo.creditType === FINANCIAL_CREDIT_TYPES.fullCredit}
									>
										<FormControlLabel
												value="NO"
												label={t("common.button.no")}
												control={<Radio />}
										/>
										<FormControlLabel
												value="YES"
												label={t("common.button.yes")}
												control={<Radio />}
										/>
								</FormRadioGroup>
							</Grid>}

							{props.keyLess && <Grid item xs={6}>
								<FormRadioGroup<IRaFillingForm>
									name="keyLess"
									label={t('raFilling.generalInfo.form.label.keyLess')}
									control={keyLessData.control}
									disabled
									>
										<FormControlLabel
												value="NO"
												label={t("common.button.no")}
												control={<Radio />}
										/>
										<FormControlLabel
												value="YES"
												label={t("common.button.yes")}
												control={<Radio />}
										/>
								</FormRadioGroup>
							</Grid>}

							{props.keyLess && <Grid item xs={6}>
								<FormTextField<IRaFillingForm>
									name="keyLessInfo"
									label={t("raFilling.generalInfo.form.label.keyLessInfo")}
									control={keyLessData.control}
									disabled
								/>
							</Grid>}

							<Grid item xs={6}>
								<FormTextField<IRaFillingForm>
									name="generalInfo.billingCarGroup"
									label={t("raFilling.generalInfo.form.label.billingAcriss")}
									control={raFillingDataForm.control}
									fullWidth
									disabled
								/>
							</Grid>

						</Grid>
					</Grid>

					<VehicleDetails/>

				</AccordionDetails>
			</Accordion>
		</>
	)
}

export default GeneralInfo;
